import React from 'react';
import { Building2, Users, CalendarClock, ShieldCheck, Phone, Mail } from 'lucide-react';
import { TrustRow, WhyChoose, OurServices, GoogleReviews } from '../components/SharedSections';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY, trackPhoneClick } from '../utils/tracking';

const highlights = [
  { icon: Users, titleRo: 'Transport zilnic angajați', titleEn: 'Daily staff transport', descRo: 'Curse regulate casă–birou–fabrică, pe trasee și orare stabilite împreună cu compania ta.', descEn: 'Regular home–office–plant routes, on schedules and itineraries agreed with your company.' },
  { icon: CalendarClock, titleRo: 'Program flexibil, în ture', titleEn: 'Flexible shift schedules', descRo: 'Acoperim schimburi de zi, de noapte și weekend, cu șoferi dedicați și vehicule rezervate.', descEn: 'We cover day, night and weekend shifts with dedicated drivers and reserved vehicles.' },
  { icon: Building2, titleRo: 'Contract & facturare lunară', titleEn: 'Contract & monthly invoicing', descRo: 'Un singur partener, un singur contract, raportare clară și facturare lunară pentru departamentul financiar.', descEn: 'One partner, one contract, clear reporting and monthly invoicing for your finance team.' },
  { icon: ShieldCheck, titleRo: 'Siguranță & punctualitate', titleEn: 'Safety & punctuality', descRo: 'Flotă modernă Mercedes, întreținută riguros, șoferi profesioniști și monitorizare a curselor.', descEn: 'Modern, rigorously maintained Mercedes fleet, professional drivers and trip monitoring.' },
];

const CorporatePage = ({ language }) => {
  const isRo = language === 'ro';

  const handlePhoneClick = () => {
    trackPhoneClick();
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  return (
    <>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-28 sm:pt-0" data-testid="corporate-hero">
        <div className="absolute inset-0">
          <img src="/images/slide-corporate.webp" alt={isRo ? 'Transport corporate Montréon' : 'Montréon corporate transport'} className="w-full h-full object-cover" />
          <div className="absolute inset-0 md:hidden" style={{ backgroundColor: 'rgba(255,255,255,0.82)' }}></div>
          <div className="absolute inset-0 hidden md:block" style={{ background: 'linear-gradient(to right, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.90) 35%, rgba(255,255,255,0.60) 60%, rgba(255,255,255,0) 100%)' }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 relative z-10 py-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-[#0D1B3E] text-white text-xs font-semibold tracking-[0.15em] uppercase px-4 py-2 rounded-full mb-5" data-testid="corporate-coming-soon-badge">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></span>
              {isRo ? 'În curând' : 'Coming soon'}
            </span>
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Soluții de mobilitate pentru companii' : 'Mobility solutions for companies'}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0D1B3E] mb-4 leading-tight">
              {isRo ? 'Transport Corporate' : 'Corporate Transport'}
            </h1>
            <p className="font-body text-lg text-gray-600 mb-8 leading-relaxed max-w-lg">
              {isRo
                ? 'Transport zilnic pentru angajații și echipele companiilor din Brașov și împrejurimi. Trasee fixe, orare adaptate turelor, un singur partener de încredere.'
                : 'Daily transport for the employees and teams of companies in Brașov and surroundings. Fixed routes, shift-adapted schedules, one trusted partner.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`tel:${PHONE_NUMBER}`} onClick={(e) => { e.preventDefault(); handlePhoneClick(); }}
                data-testid="corporate-cta-phone"
                className="inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-8 h-14 text-base rounded shadow-lg hover:shadow-xl transition-all">
                <Phone size={18} /> {PHONE_NUMBER_DISPLAY}
              </a>
              <a href="mailto:contact@montreon.ro?subject=Transport%20Corporate"
                data-testid="corporate-cta-email"
                className="inline-flex items-center justify-center gap-2 bg-[#0D1B3E] hover:bg-[#1A3A6B] text-white font-semibold px-8 h-14 text-base rounded shadow-lg hover:shadow-xl transition-all">
                <Mail size={18} /> {isRo ? 'Solicită o ofertă' : 'Request a quote'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustRow isRo={isRo} />

      <section className="py-20 sm:py-28 bg-white" data-testid="corporate-intro">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              {isRo ? 'Pentru companii' : 'For companies'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0D1B3E] mb-5">
              {isRo ? 'Angajații tăi ajung la timp. În fiecare zi.' : 'Your people arrive on time. Every day.'}
            </h2>
            <p className="font-body text-base text-gray-500 leading-relaxed">
              {isRo
                ? 'Pregătim un serviciu dedicat de transport pentru personalul companiilor: navete zilnice între localități și punctele de lucru, transport în ture pentru fabrici și centre logistice, deplasări pentru delegații și echipe de proiect. Lucrăm la detaliile finale și vom lansa oferta completă în curând.'
                : 'We are preparing a dedicated transport service for company staff: daily commutes between towns and work sites, shift transport for plants and logistics centres, trips for delegations and project teams. We are finalising the details and will launch the full offer soon.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => {
              const Icon = h.icon;
              return (
                <div key={i} className="rounded-xl border border-gray-200 p-6 hover:border-[#10B981]/50 hover:shadow-lg transition-all" data-testid={`corporate-highlight-${i}`}>
                  <div className="w-11 h-11 rounded-full bg-[#ECFDF5] flex items-center justify-center mb-4">
                    <Icon className="text-[#10B981]" size={20} />
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#0D1B3E] mb-2">{isRo ? h.titleRo : h.titleEn}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{isRo ? h.descRo : h.descEn}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-14 rounded-xl p-8 sm:p-10 text-center" style={{ backgroundColor: '#0D1B3E' }} data-testid="corporate-coming-soon-box">
            <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">{isRo ? 'În curând' : 'Coming soon'}</p>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3">
              {isRo ? 'Oferta completă pentru companii se lansează în curând' : 'The full corporate offer is launching soon'}
            </h3>
            <p className="text-white/60 text-sm sm:text-base max-w-xl mx-auto mb-6">
              {isRo
                ? 'Dacă ai deja o nevoie concretă de transport pentru angajați, scrie-ne și revenim cu o propunere personalizată.'
                : 'If you already have a concrete staff-transport need, write to us and we will come back with a tailored proposal.'}
            </p>
            <a href="mailto:contact@montreon.ro?subject=Transport%20Corporate" data-testid="corporate-coming-soon-cta"
              className="inline-flex items-center justify-center bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-8 h-12 text-sm rounded shadow-lg transition-all">
              contact@montreon.ro
            </a>
          </div>
        </div>
      </section>

      <WhyChoose isRo={isRo} />
      <OurServices isRo={isRo} />
      <GoogleReviews isRo={isRo} />
    </>
  );
};

export default CorporatePage;
